import classNames from 'classnames'
import { fieldInputClass } from 'components/common/field/fieldInputStyles'
import OsmAddressSearchInput from 'components/common/field/OsmAddressSearchInput'
import IconRender from 'components/common/IconRender'
import { AddressData } from 'newTypes/location'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomColorsRef, IconSizes } from 'types/general'
import { ParsedOsmAddress } from 'types/osmAddress'

interface AddressAutocompleteProps {
  name: string
  label: string
  value: AddressData | null
  showIcon?: boolean
  isFirstInput: boolean
  onPlaceSelect: (address: AddressData | null) => void
  customFieldStyle?: string
  readOnly?: boolean
  highlight?: boolean
  placeholder?: string
}

function addressInputDisplay(value: AddressData | null): string {
  if (!value) return ''
  if (value.formatted_address?.trim()) return value.formatted_address.trim()
  if (value.street?.trim()) {
    const withNumber = [value.number, value.street]
      .filter((part): part is string => Boolean(part?.trim()))
      .join(' ')
      .trim()
    return withNumber || value.street.trim()
  }
  return [value.city, value.province, value.zip_code]
    .filter((part): part is string => Boolean(part?.trim()))
    .join(', ')
}

function freeTextAddress(text: string, previous: AddressData | null): AddressData {
  const trimmed = text.trim()
  return {
    ...(previous || {}),
    street: trimmed,
    formatted_address: trimmed,
  }
}

function toAddressData(parsed: ParsedOsmAddress): AddressData {
  return {
    city: parsed.city,
    country: parsed.country,
    province: parsed.province,
    street: parsed.street,
    number: parsed.number,
    zip_code: parsed.postalCode,
    formatted_address: parsed.formattedAddress,
    location: parsed.location,
    geometry: {
      location: parsed.location,
    },
  }
}

export const AddressAutocomplete = ({
  name,
  label,
  value,
  isFirstInput,
  onPlaceSelect,
  showIcon = true,
  customFieldStyle,
  readOnly,
  highlight,
  placeholder,
}: AddressAutocompleteProps) => {
  const { t } = useTranslation()
  const displayFromValue = addressInputDisplay(value)
  const [streetValue, setStreetValue] = useState(displayFromValue)
  const addressPlaceholder = placeholder ?? t('auth.addressPlaceholder')

  useEffect(() => {
    setStreetValue(displayFromValue)
  }, [displayFromValue])

  useEffect(() => {
    if (isFirstInput) {
      const el = document.getElementById(name) as HTMLInputElement | null
      el?.focus()
    }
  }, [isFirstInput, name])

  const handleSelect = (parsed: ParsedOsmAddress) => {
    const next = toAddressData(parsed)
    setStreetValue(addressInputDisplay(next))
    onPlaceSelect(next)
  }

  const commitTypedAddress = () => {
    const trimmed = streetValue.trim()
    if (!trimmed) {
      if (value) onPlaceSelect(null)
      return
    }
    if (trimmed === displayFromValue) return
    onPlaceSelect(freeTextAddress(trimmed, value))
  }

  if (readOnly) {
    return (
      <div className="relative my-2">
        <label
          htmlFor={name}
          className="block text-imovSecondaryDark font-bold pl-1 mb-0.5 text-md sm:text-base"
        >
          {label}
        </label>
        <div className="relative">
          {showIcon && (
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <IconRender
                icon="regular-location-dot"
                size={IconSizes.LG}
                color={
                  highlight ? CustomColorsRef.PrimaryLight : CustomColorsRef.Gray
                }
              />
            </span>
          )}
          <input
            type="text"
            id={name}
            name={name}
            readOnly
            value={addressInputDisplay(value) || streetValue}
            title={value?.formatted_address || streetValue}
            className={classNames(
              fieldInputClass({ withIcon: showIcon }),
              customFieldStyle
            )}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="relative z-30 my-2">
      <label
        htmlFor={name}
        className="mb-0.5 block pl-1 text-md font-bold text-imovSecondaryDark sm:text-base"
      >
        {label}
      </label>
      <div className="relative">
        {showIcon ? (
          <span className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2">
            <IconRender
              icon="regular-location-dot"
              size={IconSizes.LG}
              color={highlight ? CustomColorsRef.PrimaryLight : CustomColorsRef.Gray}
            />
          </span>
        ) : null}
        <OsmAddressSearchInput
          id={name}
          label={label}
          showLabel={false}
          value={streetValue}
          placeholder={addressPlaceholder}
          inputClassName={classNames(
            fieldInputClass({ withIcon: showIcon }),
            customFieldStyle
          )}
          inputTitle={value?.formatted_address || streetValue}
          onValueChange={setStreetValue}
          onBlur={commitTypedAddress}
          onAddressSelect={handleSelect}
        />
      </div>
    </div>
  )
}

export default AddressAutocomplete
